/**
 * Product page islands: <media-gallery>, <variant-picker>,
 * <selling-plan-picker>, <product-form>.
 * Variant + allocation data is embedded as JSON; price/availability/media
 * update client-side, the URL tracks the variant, and selling-plan prices
 * recompute on both variant and plan changes.
 */
import { publish, subscribe, PUB_SUB_EVENTS, formatMoney } from './global.js';
import { connectSubscriptionOwner } from './subscription-owner.js';

/* ---------- <media-gallery> (defined first: variant-picker calls .show) ---------- */
class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.items = this.querySelectorAll('[data-media-id]');
    this.thumbs = this.querySelectorAll('[data-thumb-media-id]');
    this.announcer = this.querySelector('[data-gallery-status]');
    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.show(Number(thumb.dataset.thumbMediaId)))
    );
  }

  show(mediaId) {
    let index = 0;
    this.items.forEach((item, i) => {
      const active = Number(item.dataset.mediaId) === mediaId;
      item.classList.toggle('hidden', !active);
      if (active) index = i;
    });
    this.thumbs.forEach((thumb) => {
      const active = Number(thumb.dataset.thumbMediaId) === mediaId;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    if (this.announcer) {
      const template = window.shorelineStrings?.imageIndex || 'Image [index] of [total]';
      this.announcer.textContent = template
        .replace('[index]', index + 1)
        .replace('[total]', this.items.length);
    }
  }
}
if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

/* ---------- <variant-picker> ---------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.allocations = JSON.parse(this.querySelector('[data-allocations]').textContent);
    this.productRoot = this.closest('.main-product') || document;
    this.addEventListener('change', this.onChange.bind(this));
  }

  get selectedOptions() {
    return [...this.querySelectorAll('input[type="radio"]:checked')]
      .sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition)
      .map((input) => input.value);
  }

  onChange() {
    const options = this.selectedOptions;
    const variant = this.variants.find((v) =>
      v.options.every((value, index) => value === options[index])
    );
    const allocation = variant ? this.allocations.find((a) => a.id === variant.id) : null;

    const idInput = this.productRoot.querySelector('.product-variant-id');
    const submit = this.productRoot.querySelector('.product-form__submit');
    const submitText = submit?.querySelector('[data-atc-text]');

    if (!variant) {
      if (submit) {
        submit.disabled = true;
        if (submitText) submitText.textContent = window.variantStrings?.unavailable || 'Unavailable';
      }
      publish(PUB_SUB_EVENTS.variantChange, { variant: null, allocation: null });
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    if (idInput) {
      idInput.value = variant.id;
      idInput.disabled = !variant.available;
    }
    if (submit) {
      submit.disabled = !variant.available;
      if (submitText) {
        submitText.textContent = variant.available
          ? window.variantStrings?.addToCart || 'Add to cart'
          : window.variantStrings?.soldOut || 'Sold out';
      }
    }

    this.updatePrice(variant);

    if (variant.featured_media_id) {
      this.productRoot.querySelector('media-gallery')?.show?.(variant.featured_media_id);
    }

    publish(PUB_SUB_EVENTS.variantChange, { variant, allocation });
  }

  updatePrice(variant) {
    const priceEl = this.productRoot.querySelector('[data-main-price]');
    if (!priceEl) return;
    const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
    priceEl.querySelector('.price__regular, .price__sale').textContent = formatMoney(variant.price);
    const compare = priceEl.querySelector('.price__compare');
    if (compare) {
      compare.hidden = !sale;
      if (sale) compare.textContent = formatMoney(variant.compare_at_price);
    }
    priceEl.classList.toggle('price--on-sale', Boolean(sale));

    // The derived per-ounce line is size-specific, so it has to move with the
    // variant. Sizes that do not parse (merch, "Small") hide it instead.
    const unitEl = priceEl.querySelector('[data-derived-unit]');
    if (unitEl) {
      const ounces = parseSizeOunces(variant.option1);
      unitEl.hidden = !ounces;
      if (ounces) {
        unitEl.querySelector('[data-derived-unit-amount]').textContent = formatMoney(
          Math.round(variant.price / ounces)
        );
      }
    }
  }
}

/* "5 lb" -> 80, "12 oz" -> 12, anything else -> 0. Mirrors the Liquid in
   snippets/price.liquid; both read the size option rather than the recorded
   shipping weight, which disagrees with the bags. */
function parseSizeOunces(sizeText) {
  const text = String(sizeText || '').toLowerCase().trim();
  const amount = parseFloat(text);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  if (text.includes('lb')) return amount * 16;
  if (text.includes('oz')) return amount;
  return 0;
}
if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

/* ---------- <selling-plan-picker> ---------- */
class SellingPlanPicker extends HTMLElement {
  connectedCallback() {
    this.hiddenInput = this.querySelector('[data-selling-plan-input]');
    this.announce = this.querySelector('[data-plan-announce]');

    this.addEventListener('change', this.onChange.bind(this));
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ allocation }) => {
      if (allocation) this.refreshPrices(allocation);
    });

    const params = new URLSearchParams(window.location.search);
    const planParam = params.get('selling_plan');
    if (planParam) {
      this.selectPlan(planParam, { announce: false });
    } else {
      // A requires_selling_plan product renders its plan radio already checked,
      // but the hidden input renders empty — no allocation is selected
      // server-side until the URL carries one. Syncing on connect stops the
      // form posting selling_plan='' while the UI shows a subscription chosen.
      this.syncHiddenInput({ announce: false });
    }
    this.disconnectSubscriptionOwner = connectSubscriptionOwner(this);
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    this.disconnectSubscriptionOwner?.();
  }

  selectPlan(planId, options) {
    const option = [...this.querySelectorAll('[data-plan-select] option')].find((o) => o.value === planId);
    if (option) {
      option.selected = true;
      const groupId = option.closest('[data-plan-select]').dataset.groupId;
      const radio = this.querySelector(`[data-plan-radio][data-group-id="${groupId}"]`);
      if (radio) radio.checked = true;
    } else {
      const radio = [...this.querySelectorAll('[data-plan-radio]')].find((r) => r.value === planId);
      if (radio) radio.checked = true;
    }
    this.syncHiddenInput(options);
  }

  onChange(event) {
    if (event.target.matches('[data-plan-select]')) {
      const radio = this.querySelector(`[data-plan-radio][data-group-id="${event.target.dataset.groupId}"]`);
      if (radio) radio.checked = true;
      // The yearly figure depends on cadence, so switching Weekly -> Monthly
      // must re-derive it or the line overstates the saving by a factor of 4.
      const saveEl = this.querySelector(`[data-plan-save][data-group-id="${event.target.dataset.groupId}"]`);
      if (saveEl) {
        saveEl.dataset.ordersPerYear = SellingPlanPicker.ordersPerYear(
          event.target.selectedOptions[0]?.textContent || ''
        );
        this.renderSavings(saveEl, Number(saveEl.dataset.savedCents));
      }
    }
    this.syncHiddenInput();
  }

  // Mirrors the Liquid parser in selling-plan-picker.liquid: a cadence the
  // pattern doesn't recognise returns 0 and the copy falls back to the
  // per-order amount rather than guessing.
  static ordersPerYear(planName) {
    const name = planName.toLowerCase();
    if (name.includes('every')) return 0;
    if (name.includes('bi-week') || name.includes('biweek')) return 26;
    if (name.includes('week')) return 52;
    if (name.includes('month')) return 12;
    return 0;
  }

  renderSavings(el, savedCents) {
    if (!(savedCents > 0)) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    const perYear = Number(el.dataset.ordersPerYear) || 0;
    const yearTemplate = window.shorelineStrings?.subscriptionSavingsYear;
    const orderTemplate = window.shorelineStrings?.subscriptionSavings;
    if (perYear > 0 && yearTemplate) {
      el.textContent = yearTemplate
        .replace('[amount]', formatMoney(savedCents))
        .replace('[year]', formatMoney(savedCents * perYear));
    } else if (orderTemplate) {
      el.textContent = orderTemplate.replace('[amount]', formatMoney(savedCents));
    }
  }

  syncHiddenInput({ announce = true } = {}) {
    const checked = this.querySelector('[data-plan-radio]:checked');
    if (!checked) {
      this.hiddenInput.value = '';
      return;
    }
    let planId = checked.value;
    if (checked.dataset.groupId) {
      const select = this.querySelector(`[data-plan-select][data-group-id="${checked.dataset.groupId}"]`);
      if (select) planId = select.value;
    }
    this.hiddenInput.value = planId;
    this.querySelectorAll('.selling-plan-picker__option').forEach((option) => {
      option.classList.toggle('is-selected', option.contains(checked));
    });
    if (this.announce && announce) {
      this.announce.textContent = planId
        ? window.shorelineStrings?.subscriptionSelected || 'Subscription selected. Price updated.'
        : window.shorelineStrings?.onetimeSelected || 'One-time purchase selected. Price updated.';
    }
  }

  refreshPrices(allocationData) {
    const onetime = this.querySelector('[data-onetime-price]');
    if (onetime) onetime.textContent = formatMoney(allocationData.price);
    this.querySelectorAll('[data-plan-price]').forEach((el) => {
      const groupAllocation = allocationData.allocations.find((a) => a.group_id === el.dataset.groupId);
      if (groupAllocation) el.textContent = formatMoney(groupAllocation.price);
    });
    // The dollar saving is variant-specific, so it has to move with the price
    // or a 5 lb bag inherits the 12 oz bag's number.
    this.querySelectorAll('[data-plan-save]').forEach((el) => {
      const groupAllocation = allocationData.allocations.find((a) => a.group_id === el.dataset.groupId);
      if (!groupAllocation) return;
      const saved = allocationData.price - groupAllocation.price;
      el.dataset.savedCents = saved;
      this.renderSavings(el, saved);
    });
  }
}
if (!customElements.get('selling-plan-picker')) customElements.define('selling-plan-picker', SellingPlanPicker);

/* ---------- <product-form> ---------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.submitText = this.submitButton?.querySelector('[data-atc-text]');
    this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    const restingLabel = this.submitText.dataset.restingLabel || this.submitText.textContent;
    this.submitText.dataset.restingLabel = restingLabel;

    this.errorWrapper.hidden = true;
    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.classList.add('is-loading');

    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(this.form),
      });
      const data = await response.json();

      if (data.status) {
        this.showError(data.description || data.message);
        return;
      }

      const cartResponse = await fetch(`${window.routes.cart_url}.js`);
      const cart = await cartResponse.json();
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', item: data, cart });

      this.submitText.textContent = window.variantStrings?.added || '✓ Added';
      clearTimeout(this.restoreTimer);
      this.restoreTimer = setTimeout(() => {
        this.submitText.textContent = this.submitText.dataset.restingLabel;
      }, 1800);

      if (window.shorelineSettings?.cartType === 'page') {
        window.location.href = window.routes.cart_url;
      }
    } catch (error) {
      this.showError(window.shorelineStrings?.addToCartError || 'Something went wrong. Please try again.');
    } finally {
      this.submitButton.removeAttribute('aria-disabled');
      this.submitButton.classList.remove('is-loading');
    }
  }

  showError(message) {
    this.errorWrapper.hidden = false;
    this.errorWrapper.querySelector('.product-form__error-message').textContent = message;
  }
}
if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

/* ---------- Free-shipping distance on the buy box ----------
   Hydrates the static threshold sentence into the visitor's live distance
   ("You're $12.50 away from free shipping"). Client-side on purpose: the
   storefront HTML cache is shared across sessions, so this math can never
   be server-rendered without showing someone else's cart. Failure of any
   kind leaves the static sentence untouched. */
(function hydrateShippingDistance() {
  const el = document.querySelector('[data-shipping-distance]');
  if (!el) return;
  const threshold = parseInt(el.dataset.thresholdCents, 10);
  if (!threshold) return;

  const render = (totalCents) => {
    if (typeof totalCents !== 'number') return;
    const remaining = threshold - totalCents;
    if (remaining > 0) {
      const template =
        window.shorelineStrings?.freeShippingRemaining || "You're [remaining] away from free shipping";
      el.textContent = `${template.replace('[remaining]', formatMoney(remaining))}.`;
    } else {
      el.textContent = `${window.shorelineStrings?.freeShippingUnlocked || "You've unlocked free shipping"}.`;
    }
  };

  fetch(`${window.routes?.cart_url || '/cart'}.js`)
    .then((r) => (r.ok ? r.json() : null))
    .then((cart) => cart && render(cart.total_price))
    .catch(() => {});

  /* Adding to cart from this page updates the line in place. */
  subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cart }) => {
    if (cart) render(cart.total_price);
  });
})();

/**
 * <sticky-atc> — mobile add-to-cart bar.
 *
 * Owns no cart logic. Its button submits the real product form via the form
 * attribute, and everything it displays is mirrored from the real buy box
 * rather than recomputed, so sold-out states, the "Added" morph and every
 * variant or selling-plan price change follow automatically and cannot
 * disagree with the button they shadow.
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    const sectionId = this.dataset.sectionId;
    this.root = this.closest('.main-product') || document;
    this.realButton = this.root.querySelector(`#ProductSubmitButton-${sectionId}`);
    this.realText = this.realButton?.querySelector('[data-atc-text]');
    this.realPrice = this.root.querySelector('[data-main-price]');

    this.button = this.querySelector('[data-sticky-submit]');
    this.text = this.querySelector('[data-sticky-text]');
    this.price = this.querySelector('[data-sticky-price]');

    /* Without a real button there is nothing to shadow, and an uncaught error
       here would fail the whole page. */
    if (!this.realButton || !this.button) return;

    this.hidden = false;
    this.syncState();
    this.syncPrice();

    this.stateObserver = new MutationObserver(() => this.syncState());
    this.stateObserver.observe(this.realButton, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
      characterData: true,
    });

    if (this.realPrice) {
      this.priceObserver = new MutationObserver(() => this.syncPrice());
      this.priceObserver.observe(this.realPrice, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    /* Visible only once the real button has scrolled off the TOP. Checking the
       bounding rect as well as intersection keeps the bar down on first paint
       and on pages short enough that the button never leaves the viewport. */
    this.buttonObserver = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.toggle(scrolledPast && !this.footerVisible);
      },
      { threshold: 0 }
    );
    this.buttonObserver.observe(this.realButton);

    /* Never cover the footer: the newsletter form and the address live there. */
    const footer = document.querySelector('.footer, footer');
    if (footer) {
      this.footerObserver = new IntersectionObserver(
        ([entry]) => {
          this.footerVisible = entry.isIntersecting;
          if (this.footerVisible) this.toggle(false);
        },
        { threshold: 0 }
      );
      this.footerObserver.observe(footer);
    }
  }

  disconnectedCallback() {
    this.stateObserver?.disconnect();
    this.priceObserver?.disconnect();
    this.buttonObserver?.disconnect();
    this.footerObserver?.disconnect();
  }

  toggle(show) {
    this.classList.toggle('is-visible', Boolean(show));
  }

  syncState() {
    if (!this.realButton || !this.button) return;
    this.button.disabled = this.realButton.disabled;
    const label = this.realText?.textContent?.trim();
    if (label && this.text) this.text.textContent = label;
  }

  syncPrice() {
    if (!this.realPrice || !this.price) return;
    const regular = this.realPrice.querySelector('.price__regular, .price__sale');
    const value = regular?.textContent?.trim();
    if (value) this.price.textContent = value;
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
